// frontend\src\pages\DevChart.js
import { useState, useEffect } from "react";
import {
  GET_FILMS_API,
  GET_PROCESS_API,
  GET_DEVELOPERS_API,
} from "../constants";
import { Link, Navigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../api/api";
import { useAuth } from "../hooks/useAuth";
import ScrollToTopButton from "../components/ScrollToTopButton";
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { BiCopy, BiStar, BiSolidStar } from "react-icons/bi";

const DevChart = () => {
  const { isAuthenticated } = useAuth();
  
  const [films, setFilms] = useState([]);
  const [developers, setDevelopers] = useState([]);
  const [selectedFilm, setSelectedFilm] = useState("");
  const [selectedDeveloper, setSelectedDeveloper] = useState("");
  const [results, setResults] = useState([]);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [dilutionFilter, setDilutionFilter] = useState("");
  const [isoFilter, setIsoFilter] = useState("");
  const [favorites, setFavorites] = useState([]);

  // Загружаем сохраненное состояние при монтировании
  useEffect(() => {
    const savedState = localStorage.getItem('devChartState');
    if (savedState) {
      const state = JSON.parse(savedState);
      setSelectedFilm(state.selectedFilm || "");
      setSelectedDeveloper(state.selectedDeveloper || "");
      setResults(state.results || []);
      setDilutionFilter(state.dilutionFilter || "");
      setIsoFilter(state.isoFilter || "");
    }
  }, []);

  // Сохраняем состояние при изменении
  useEffect(() => {
    const state = {
      selectedFilm,
      selectedDeveloper,
      results,
      dilutionFilter,
      isoFilter
    };
    localStorage.setItem('devChartState', JSON.stringify(state));
  }, [selectedFilm, selectedDeveloper, results, dilutionFilter, isoFilter]);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [filmsResponse, developersResponse] = await Promise.all([
          api.get(GET_FILMS_API),
          api.get(GET_DEVELOPERS_API),
        ]);

        const filmsData = Array.isArray(filmsResponse?.data?.results)
          ? filmsResponse.data.results
          : Array.isArray(filmsResponse?.data)
          ? filmsResponse.data
          : [];

        const developersData = Array.isArray(developersResponse?.data?.results)
          ? developersResponse.data.results
          : Array.isArray(developersResponse?.data)
          ? developersResponse.data
          : [];

        setFilms(filmsData);
        setDevelopers(developersData);
        setIsDataLoaded(true);
      } catch (error) {
        console.error(error);
        setError("Ошибка при загрузке данных");
        toast.error("Ошибка при загрузке данных. Проверьте подключение к Интернету.");
      }
    };

    loadInitialData();
  }, []);

  useEffect(() => {
    const storedFavorites = JSON.parse(localStorage.getItem("devFavorites") || "[]");
    setFavorites(storedFavorites);
  }, []);

  const handleSearch = async () => {
    if (!selectedFilm || !selectedDeveloper) {
      toast.error("Пожалуйста, выберите плёнку и проявитель.");
      setResults([]);
      return;
    }

    if (selectedFilm === "all" && selectedDeveloper === "all") {
      toast.error("Выберите конкретную пленку или проявитель.");
      setResults([]);
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const response = await api.get(GET_PROCESS_API, {
        params: {
          film_id: selectedFilm,
          developer_id: selectedDeveloper,
        },
      });

      const responseData = Array.isArray(response?.data?.results)
        ? response.data.results
        : Array.isArray(response?.data)
        ? response.data
        : [];

      setResults(responseData);
    } catch (error) {
      console.error(error);
      setError("Ошибка при выполнении поиска");
      toast.error("Ошибка при загрузке данных. Проверьте подключение к Интернету");
    } finally {
      setIsLoading(false);
    }
  };

  const filteredResults = results.filter(result => {
    const matchesDilution = !dilutionFilter || 
      result.diluton?.toLowerCase().includes(dilutionFilter.toLowerCase());
    const matchesISO = !isoFilter || result.iso === isoFilter;
    return matchesDilution && matchesISO;
  });

  const uniqueISOs = Array.from(new Set(filteredResults.map(r => r.iso)))
    .sort((a, b) => a - b);

  const parseSimpleTime = (timeStr) => {
    if (!timeStr) return 0;
    
    const str = timeStr.toString().trim();
    
    if (str.includes(":")) {
      const [mins, secs] = str.split(":").map(Number);
      return mins + secs / 60;
    }
    
    const num = parseFloat(str);
    return isNaN(num) ? 0 : num;
  };

  const parseTime = (timeStr) => {
    if (!timeStr) return 0;

    const str = timeStr.toString().trim();

    if (str.includes("-") && str.includes(":")) {
      const parts = str.split("-").map(part => part.trim());
      if (parts.length === 2) {
        const time1 = parseSimpleTime(parts[0]);
        const time2 = parseSimpleTime(parts[1]);
        if (time1 > 0 && time2 > 0) {
          return (time1 + time2) / 2;
        }
      }
    }

    if (str.includes(":")) {
      const [mins, secs] = str.split(":").map(Number);
      return mins + secs / 60;
    }

    if (str.includes("-")) {
      const [min, max] = str.split("-").map(s => parseFloat(s.trim()));
      if (!isNaN(min) && !isNaN(max)) {
        return (min + max) / 2;
      }
    }

    const num = parseFloat(str);
    return isNaN(num) ? 0 : num;
  };

  const getDilutionTooltip = (dilution) => {
    if (!dilution) return "Разбавление не указано";
    
    const lowerDilution = dilution.toLowerCase();
    if (lowerDilution === "stock") {
      return "Используется без разбавления (концентрат)";
    }
    
    const parts = dilution.split('+').map(part => part.trim());
    if (parts.length >= 2) {
      const developerPart = parts[0];
      const waterParts = parts.slice(1).join(' + ');
      return `${developerPart} часть(и) проявителя + ${waterParts} часть(и) воды`;
    }
    
    return dilution;
  };

  const toggleFavorite = (result) => {
    const key = `favorite_${result.film.film_id}_${result.developer.dev_id}_${result.diluton}`;
    let newFavorites = [...favorites];
    
    if (newFavorites.some(f => f.key === key)) {
      newFavorites = newFavorites.filter(f => f.key !== key);
    } else {
      newFavorites.push({ key, ...result });
    }
    
    setFavorites(newFavorites);
    localStorage.setItem("devFavorites", JSON.stringify(newFavorites));
  };

  const isFavorite = (result) => {
    const key = `favorite_${result.film.film_id}_${result.developer.dev_id}_${result.diluton}`;
    return favorites.some(f => f.key === key);
  };

  const copyRecipe = (result) => {
    const text = `${result.film.film_name} + ${result.developer.dev_name} (${result.diluton})\nISO: ${result.iso}\nВремя: ${result.time_135} (135), ${result.time_120} (120)\nТемпература: ${result.temp_c}°C\n${result.notes || ""}`;
    navigator.clipboard.writeText(text);
    toast.success("Рецепт скопирован!");
  };

  const getCalcState = (result, timeField, timeValue) => ({
    calculator: 'temperature',
    baseTime: parseTime(timeValue),
    baseTemp: result.temp_c,
    filmType: 'bw'
  });

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return (
    <div className="container-fluid my-4">
      <h1 className="mb-4">База знаний</h1>

      <div className="row">
        <div className="col-md-2 mb-4">
          <div className="mb-3">
            <label htmlFor="filmSelect" className="form-label">Плёнка:</label>
            <select
              id="filmSelect"
              className="form-select"
              value={selectedFilm}
              onChange={(e) => setSelectedFilm(e.target.value)}
              disabled={!isDataLoaded}
            >
              <option value="">Выберите плёнку</option>
              <option value="all">Все плёнки</option>
              <option disabled>---</option>
              {films.map((film) => (
                <option key={film.film_id} value={film.film_id}>
                  {film.film_name}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-3">
            <label htmlFor="developerSelect" className="form-label">Проявитель:</label>
            <select
              id="developerSelect"
              className="form-select"
              value={selectedDeveloper}
              onChange={(e) => setSelectedDeveloper(e.target.value)}
              disabled={!isDataLoaded}
            >
              <option value="">Выберите проявитель</option>
              <option value="all">Все проявители</option>
              <option disabled>---</option>
              {developers.map((developer) => (
                <option key={developer.dev_id} value={developer.dev_id}>
                  {developer.dev_name}
                </option>
              ))}
            </select>
          </div>

          <button
            className="btn btn-primary w-100"
            onClick={handleSearch}
            disabled={!isDataLoaded || isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Загрузка...
              </>
            ) : (
              "Поиск"
            )}
          </button>
        </div>

        <div className="col-md-10">
          {results.length > 0 && (
            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Поиск по разбавлению (1+1)"
                  value={dilutionFilter}
                  onChange={(e) => setDilutionFilter(e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <select
                  className="form-select"
                  value={isoFilter}
                  onChange={(e) => setIsoFilter(e.target.value)}
                >
                  <option value="">Все ISO</option>
                  {uniqueISOs.map(iso => (
                    <option key={iso} value={iso}>{iso}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              {filteredResults.length > 0 && (
                <span className="text-muted">Найдено: {filteredResults.length}</span>
              )}
            </div>
            <Link to="/favorites" className="btn btn-outline-warning btn-sm">
              <BiSolidStar className="me-1" />
              Избранное ({favorites.length})
            </Link>
          </div>

          <div className="d-none d-lg-block">
            <div className="table-responsive">
              <table className="table table-striped table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Плёнка</th>
                    <th>Проявитель</th>
                    <th>Разбавление</th>
                    <th>ISO</th>
                    <th>Время (135)</th>
                    <th>Время (120)</th>
                    <th>Время (листовая)</th>
                    <th>t° раствора</th>
                    <th>Заметки</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredResults.map((result, index) => (
                    <tr key={index}>
                      <td>{result.film.film_name}</td>
                      <td>{result.developer.dev_name}</td>
                      <td>
                        <OverlayTrigger placement="top" overlay={<Tooltip>{getDilutionTooltip(result.diluton)}</Tooltip>}>
                          <span className="text-decoration-underline" style={{ cursor: 'help' }}>
                            {result.diluton}
                          </span>
                        </OverlayTrigger>
                      </td>
                      <td>{result.iso}</td>
                      <td>
                        <OverlayTrigger placement="top" overlay={<Tooltip>Пересчитать время при другой температуре</Tooltip>}>
                          <Link
                            to="/calc"
                            state={getCalcState(result, 'time_135', result.time_135)}
                            className="text-primary text-decoration-underline"
                          >
                            {result.time_135}
                          </Link>
                        </OverlayTrigger>
                      </td>
                      <td>
                        <OverlayTrigger placement="top" overlay={<Tooltip>Пересчитать время при другой температуре</Tooltip>}>
                          <Link
                            to="/calc"
                            state={getCalcState(result, 'time_120', result.time_120)}
                            className="text-primary text-decoration-underline"
                          >
                            {result.time_120}
                          </Link>
                        </OverlayTrigger>
                      </td>
                      <td>
                        <OverlayTrigger placement="top" overlay={<Tooltip>Пересчитать время при другой температуре</Tooltip>}>
                          <Link
                            to="/calc"
                            state={getCalcState(result, 'time_sheet', result.time_sheet)}
                            className="text-primary text-decoration-underline"
                          >
                            {result.time_sheet}
                          </Link>
                        </OverlayTrigger>
                      </td>
                      <td>{result.temp_c}</td>
                      <td>{result.notes}</td>
                      <td>
                        <div className="d-flex gap-1">
                          <OverlayTrigger placement="top" overlay={<Tooltip>Копировать рецепт</Tooltip>}>
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => copyRecipe(result)}>
                              <BiCopy />
                            </button>
                          </OverlayTrigger>
                          <OverlayTrigger placement="top" overlay={<Tooltip>{isFavorite(result) ? "Убрать из избранного" : "Добавить в избранное"}</Tooltip>}>
                            <button className="btn btn-sm btn-outline-warning" onClick={() => toggleFavorite(result)}>
                              {isFavorite(result) ? <BiSolidStar /> : <BiStar />}
                            </button>
                          </OverlayTrigger>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="d-lg-none">
            {filteredResults.map((result, index) => (
              <div key={index} className="card mb-3">
                <div className="card-body">
                  <h6 className="card-title mb-2">
                    {result.film.film_name} + {result.developer.dev_name}
                  </h6>
                  <div className="small text-muted mb-2">
                    <strong>Разбавление:</strong> {result.diluton} | <strong>ISO:</strong> {result.iso}
                  </div>
                  <div className="mb-2">
                    <strong>Время (135):</strong>{' '}
                    <Link
                      to="/calc"
                      state={getCalcState(result, 'time_135', result.time_135)}
                      className="text-primary text-decoration-underline"
                    >
                      {result.time_135}
                    </Link>
                  </div>
                  <div className="mb-2">
                    <strong>Время (120):</strong>{' '}
                    <Link
                      to="/calc"
                      state={getCalcState(result, 'time_120', result.time_120)}
                      className="text-primary text-decoration-underline"
                    >
                      {result.time_120}
                    </Link>
                  </div>
                  <div className="mb-2">
                    <strong>Время (листовая):</strong>{' '}
                    <Link
                      to="/calc"
                      state={getCalcState(result, 'time_sheet', result.time_sheet)}
                      className="text-primary text-decoration-underline"
                    >
                      {result.time_sheet}
                    </Link>
                  </div>
                  <div className="small text-muted mb-2">
                    <strong>Температура:</strong> {result.temp_c}°C
                  </div>
                  {result.notes && (
                    <p className="small text-muted fst-italic mb-2">{result.notes}</p>
                  )}
                  <div className="d-flex gap-2">
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => copyRecipe(result)}>
                      <BiCopy />
                    </button>
                    <button className="btn btn-sm btn-outline-warning" onClick={() => toggleFavorite(result)}>
                      {isFavorite(result) ? <BiSolidStar /> : <BiStar />}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredResults.length > 0 && (
            <div className="mt-3 text-muted">
              Найдено записей: {filteredResults.length}
            </div>
          )}

          {results.length === 0 && !isLoading && (
            <div className="alert alert-info mt-3">
              Нет записей для выбранного сочетания. Попробуйте другие комбинации.
            </div>
          )}

          {error && <div className="alert alert-danger mt-3">{error}</div>}
        </div>
      </div>

      <ScrollToTopButton />
    </div>
  );
};

export default DevChart;